export const ETHANOL_DENSITY_G_PER_ML = 0.78937;

export type AlcoholConsumedUnit = "mL" | "L" | "percent_container" | "fraction_container" | "containers";

export interface AlcoholDoseInput {
  containerVolumeMl: number;
  abvPercent: number;
  consumedValue: number;
  consumedUnit: AlcoholConsumedUnit;
}

export interface AlcoholDoseResult {
  containerVolumeMl: number;
  abvPercent: number;
  consumedValue: number;
  consumedUnit: AlcoholConsumedUnit;
  consumedMl: number;
  ethanolMl: number;
  ethanolGrams: number;
  containerFraction: number;
}

function finitePositive(value: number) {
  return Number.isFinite(value) && value > 0;
}

export function consumedVolumeMl(input: AlcoholDoseInput): number | null {
  if (!finitePositive(input.containerVolumeMl) || !finitePositive(input.consumedValue)) return null;
  switch (input.consumedUnit) {
    case "mL": return input.consumedValue;
    case "L": return input.consumedValue * 1000;
    case "percent_container": return input.containerVolumeMl * (input.consumedValue / 100);
    case "fraction_container": return input.containerVolumeMl * input.consumedValue;
    case "containers": return input.containerVolumeMl * input.consumedValue;
    default: return null;
  }
}

export function calculateAlcoholDose(input: AlcoholDoseInput): AlcoholDoseResult | null {
  if (!finitePositive(input.containerVolumeMl)) return null;
  if (!Number.isFinite(input.abvPercent) || input.abvPercent <= 0 || input.abvPercent > 100) return null;
  if (!finitePositive(input.consumedValue)) return null;
  if (input.consumedUnit === "percent_container" && input.consumedValue > 100) return null;
  if (input.consumedUnit === "fraction_container" && input.consumedValue > 1) return null;

  const consumedMl = consumedVolumeMl(input);
  if (consumedMl == null || consumedMl <= 0) return null;
  const ethanolMl = consumedMl * (input.abvPercent / 100);
  const ethanolGrams = ethanolMl * ETHANOL_DENSITY_G_PER_ML;

  return {
    ...input,
    consumedMl,
    ethanolMl,
    ethanolGrams,
    containerFraction: consumedMl / input.containerVolumeMl,
  };
}

export function formatAlcoholDose(result: AlcoholDoseResult) {
  const consumed = result.consumedMl >= 100 ? result.consumedMl.toFixed(0) : result.consumedMl.toFixed(1);
  const grams = result.ethanolGrams >= 10 ? result.ethanolGrams.toFixed(1) : result.ethanolGrams.toFixed(2);
  return `${grams} g de etanol · ${consumed} mL de bebida a ${result.abvPercent}% ABV`;
}
